export const RESULT_LOOKUP_TYPES = {
  generic: 'inspection_result_generic',
  body: 'inspection_result_body',
};

const SHARED_RESULT_OPTIONS = [
  {
    value: 'good',
    label: '\u062c\u064a\u062f',
    description: '',
    bgColor: '#22c55e',
    borderColor: '#15803d',
    textColor: '#ffffff',
  },
  {
    value: 'scratch',
    label: '\u062e\u062f\u0634',
    description: '',
    bgColor: '#f97316',
    borderColor: '#ea580c',
    textColor: '#ffffff',
  },
  {
    value: 'damage',
    label: '\u0636\u0631\u0631',
    description: '',
    bgColor: '#facc15',
    borderColor: '#ca8a04',
    textColor: '#111827',
  },
  {
    // مرشوش
    value: 'sprayed',
    label: '\u0645\u0631\u0634\u0648\u0634',
    description: '',
    bgColor: '#a855f7',
    borderColor: '#7e22ce',
    textColor: '#ffffff',
  },
  {
    value: 'riveted',
    label: '\u0645\u0633\u0645\u0643\u0631',
    description: '',
    bgColor: '#0ea5e9',
    borderColor: '#0369a1',
    textColor: '#ffffff',
  },
  {
    // مبدل
    value: 'replaced',
    label: '\u0645\u0628\u062f\u0644',
    description: '',
    bgColor: '#ec4899',
    borderColor: '#be185d',
    textColor: '#ffffff',
  },
  {
    // تالف
    value: 'defect',
    label: '\u062a\u0627\u0644\u0641',
    description: '',
    bgColor: '#dc2626',
    borderColor: '#991b1b',
    textColor: '#ffffff',
  },
];

export const DEFAULT_GENERIC_RESULT_OPTIONS = SHARED_RESULT_OPTIONS;

export const DEFAULT_BODY_RESULT_OPTIONS = SHARED_RESULT_OPTIONS;

// ترتيب الخطورة المعتمد: 1 = الأفضل ... 7 = الأسوأ.
// هذا هو ترتيب العرض المطلوب، وهو نفسه المستخدم في احتساب التقييم
// في الخادم، فيبقى الرقم الظاهر للمستخدم متطابقًا مع أثر الحالة عليه.
export const RESULT_SEVERITY_ORDER = [
  'good',      // 1 جيد
  'scratch',   // 2 خدش
  'damage',    // 3 ضرر
  'sprayed',   // 4 مرشوش
  'riveted',   // 5 مسمكر
  'replaced',  // 6 مبدل
  'defect',    // 7 تالف
];

// «غير محدد» ليست حالة تقييم، فلا تأخذ رقمًا.
export const UNRATED_RESULT_KEYS = ['unknown'];

/**
 * يبني خريطة { قيمة الحالة: رقمها } مرتّبة حسب الخطورة.
 * أي حالة مخصّصة يضيفها المسؤول تأخذ رقمًا بعد الحالات المعروفة.
 */
export function buildResultNumberMap(options) {
  const values = (Array.isArray(options) ? options : [])
    .map((option) => String(option?.value || '').trim())
    .filter(Boolean);

  const known = RESULT_SEVERITY_ORDER.filter((key) => values.includes(key));
  const custom = values.filter(
    (key) => !RESULT_SEVERITY_ORDER.includes(key) && !UNRATED_RESULT_KEYS.includes(key),
  );

  const ordered = [...new Set([...known, ...custom])];

  return ordered.reduce((map, key, index) => {
    const number = index + 1;
    map[key] = number;
    map[key.toLowerCase()] = number;
    return map;
  }, {});
}

/**
 * يرتّب الخيارات حسب الخطورة ويُلحق برقم كل حالة، لعرضها في الدليل.
 */
export function getNumberedResultOptions(options) {
  const numberMap = buildResultNumberMap(options);

  return (Array.isArray(options) ? options : [])
    .map((option) => ({ ...option, number: numberMap[String(option?.value || '').trim()] || null }))
    .filter((option) => option.number)
    .sort((left, right) => left.number - right.number);
}

function getDefaultOptions(kind = 'generic') {
  return kind === 'body' ? DEFAULT_BODY_RESULT_OPTIONS : DEFAULT_GENERIC_RESULT_OPTIONS;
}

const CUSTOM_RESULT_FALLBACK = {
  description: '',
  bgColor: '#64748b',
  borderColor: '#475569',
  textColor: '#ffffff',
};

const DEFAULT_LABEL_BY_VALUE = Object.fromEntries(
  SHARED_RESULT_OPTIONS.map((option) => [option.value, option.label])
);

function looksCorruptLabel(label) {
  const text = String(label || '').trim();
  if (!text) return true;
  if (/^\?+$/.test(text)) return true;
  return /[طظ]/.test(text);
}

function normalizeHexColor(color, fallback) {
  const value = String(color || '').trim();
  if (!value) return fallback;
  return value;
}

function getTextColorForBackground(hexColor, fallback = '#ffffff') {
  const color = String(hexColor || '').trim().replace('#', '');
  if (![3, 6].includes(color.length)) return fallback;

  const expanded = color.length === 3
    ? color.split('').map((char) => `${char}${char}`).join('')
    : color;

  const red = parseInt(expanded.slice(0, 2), 16);
  const green = parseInt(expanded.slice(2, 4), 16);
  const blue = parseInt(expanded.slice(4, 6), 16);

  if ([red, green, blue].some(Number.isNaN)) return fallback;

  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  return brightness > 160 ? '#111827' : '#ffffff';
}

export function getResultLookupType(kind = 'generic') {
  return RESULT_LOOKUP_TYPES[kind] || RESULT_LOOKUP_TYPES.generic;
}

export function normalizeResultOptions(rows, kind = 'generic') {
  const defaults = getDefaultOptions(kind);
  const fallbackMap = new Map(defaults.map((option) => [option.value, option]));
  const normalizedRows = Array.isArray(rows) ? rows : [];

  const fromRows = normalizedRows
    .filter((row) => String(row?.value || '').trim())
    .map((row) => {
      const value = String(row.value).trim();
      const fallback = fallbackMap.get(value) || CUSTOM_RESULT_FALLBACK;
      const bgColor = normalizeHexColor(row?.color, fallback.bgColor);
      const rowLabel = String(row?.label || '').trim();
      return {
        ...fallback,
        value,
        label: looksCorruptLabel(rowLabel)
          ? (DEFAULT_LABEL_BY_VALUE[value] || fallback.label || value)
          : rowLabel,
        description: String(row?.description || '').trim() || fallback.description,
        bgColor,
        borderColor: normalizeHexColor(row?.color, fallback.borderColor),
        textColor: getTextColorForBackground(bgColor, fallback.textColor),
        sortOrder: Number(row?.sort_order ?? 0),
      };
    })
    .sort((left, right) => left.sortOrder - right.sortOrder);

  const missingDefaults = defaults.filter((option) => !fromRows.some((row) => row.value === option.value));

  const byValue = [...fromRows, ...missingDefaults].filter((option, index, all) => (
    all.findIndex((item) => item.value === option.value) === index
  ));

  // إزالة التكرار بالاسم الظاهر أيضًا: صف في lookups بمفتاح مختلف لكن بنفس
  // الاسم كان يُنتج زرّين متطابقين في شاشة الفحص. نُبقي المفتاح المعروف
  // للنظام لأن التقييم والرسم يعتمدان عليه.
  const seenLabels = new Map();
  const merged = [];

  byValue.forEach((option) => {
    const labelKey = String(option.label || '').trim();
    if (!labelKey) {
      merged.push(option);
      return;
    }

    const existingIndex = seenLabels.get(labelKey);
    if (existingIndex === undefined) {
      seenLabels.set(labelKey, merged.length);
      merged.push(option);
      return;
    }

    const existing = merged[existingIndex];
    const existingIsKnown = RESULT_SEVERITY_ORDER.includes(existing.value);
    const currentIsKnown = RESULT_SEVERITY_ORDER.includes(option.value);

    // نستبدل المحفوظ فقط إن كان الجديد مفتاحًا معروفًا والقديم غير معروف.
    if (currentIsKnown && !existingIsKnown) {
      merged[existingIndex] = option;
    }
  });

  // نفرض ترتيب الخطورة المعتمد دائمًا حتى لو كان ترتيب جدول lookups مختلفًا،
  // وإلا ظهرت الحالات الجديدة (مرشوش/مبدل) في آخر القائمة.
  return merged.sort((left, right) => {
    const leftIndex = RESULT_SEVERITY_ORDER.indexOf(left.value);
    const rightIndex = RESULT_SEVERITY_ORDER.indexOf(right.value);
    const leftRank = leftIndex === -1 ? RESULT_SEVERITY_ORDER.length : leftIndex;
    const rightRank = rightIndex === -1 ? RESULT_SEVERITY_ORDER.length : rightIndex;
    if (leftRank !== rightRank) return leftRank - rightRank;
    // الحالات المخصّصة تحتفظ بترتيب المسؤول بينها
    return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
  });
}

// ===== دعم اختيار أكثر من نتيجة للجزء الواحد =====

/**
 * يوحّد أي شكل قادم من الواجهة أو قاعدة البيانات إلى مصفوفة حالات نظيفة
 * مرتّبة حسب الخطورة وبلا تكرار.
 */
export function normalizeStatusList(value) {
  const raw = Array.isArray(value)
    ? value
    : String(value || '')
        .split(',')
        .map((part) => part.trim());

  const cleaned = [...new Set(
    raw
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .filter((item) => !UNRATED_RESULT_KEYS.includes(item)),
  )];

  return cleaned.sort((left, right) => {
    const leftIndex = RESULT_SEVERITY_ORDER.indexOf(left);
    const rightIndex = RESULT_SEVERITY_ORDER.indexOf(right);
    return (leftIndex === -1 ? 99 : leftIndex) - (rightIndex === -1 ? 99 : rightIndex);
  });
}

/**
 * أشد الحالات المختارة خطورة — هي التي تحدّد لون الجزء ورقمه في الرسم،
 * لأن الجزء الواحد لا يمكن أن يحمل أكثر من لون.
 */
// حالات تنفي غيرها: الجزء لا يكون «جيد» و«مسمكر» في آن واحد.
// بدونها كان النقر على حالة ضرر بعد «جيد» يُضيفها فتبقى «جيد» في النتيجة.
export const EXCLUSIVE_RESULT_KEYS = ['good'];

/**
 * تبديل حالة واحدة مع احترام الحالات النافية. آخر نقرة هي التي تفوز:
 * اختيار «جيد» يلغي حالات الضرر، واختيار ضرر يلغي «جيد».
 */
export function toggleResultStatus(current, statusValue) {
  const list = normalizeStatusList(current);

  if (list.includes(statusValue)) {
    return normalizeStatusList(list.filter((entry) => entry !== statusValue));
  }

  if (EXCLUSIVE_RESULT_KEYS.includes(statusValue)) {
    return [statusValue];
  }

  return normalizeStatusList([
    ...list.filter((entry) => !EXCLUSIVE_RESULT_KEYS.includes(entry)),
    statusValue,
  ]);
}

export function mostSevereStatus(statuses) {
  const list = normalizeStatusList(statuses);
  if (!list.length) return null;

  return list.reduce((worst, current) => {
    const worstIndex = RESULT_SEVERITY_ORDER.indexOf(worst);
    const currentIndex = RESULT_SEVERITY_ORDER.indexOf(current);
    return (currentIndex === -1 ? 99 : currentIndex) > (worstIndex === -1 ? 99 : worstIndex)
      ? current
      : worst;
  }, list[0]);
}

/**
 * يقرأ حالات الجزء من أي صيغة: المصفوفة الجديدة، أو الحقل المفرد القديم،
 * أو الأعلام المنطقية القديمة (is_sprayed وأخواتها) لبيانات ما قبل التحديث.
 */
export function readStatusList(value) {
  if (!value) return [];

  if (value.condition_statuses !== undefined && value.condition_statuses !== null) {
    const parsed = Array.isArray(value.condition_statuses)
      ? value.condition_statuses
      : (() => {
          try {
            return JSON.parse(value.condition_statuses);
          } catch {
            return String(value.condition_statuses).split(',');
          }
        })();
    const list = normalizeStatusList(parsed);
    if (list.length) return list;
  }

  if (value.condition_status) {
    const list = normalizeStatusList([value.condition_status]);
    if (list.length) return list;
  }

  // توافق مع السجلات القديمة المخزّنة كأعلام منطقية
  const legacy = [];
  if (value.is_sprayed) legacy.push('sprayed');
  if (value.is_replaced) legacy.push('replaced');
  if (value.is_riveted) legacy.push('riveted');
  if (value.is_dented) legacy.push('defect');
  if (value.is_modified || value.is_loose || value.is_plastic || value.is_protected) legacy.push('damage');

  return normalizeStatusList(legacy);
}

export function buildResultOptionMap(options) {
  return Object.fromEntries((options || []).map((option) => [option.value, option]));
}

export function getAllowedResultOptions(options, allowedValues) {
  const resultOptions = Array.isArray(options) ? options : [];
  if (!Array.isArray(allowedValues) || !allowedValues.length) return resultOptions;

  const optionByKey = new Map(
    resultOptions.map((option) => [String(option.value || '').toLowerCase(), option])
  );

  return allowedValues
    .map((value) => {
      const rawValue = String(value || '').trim();
      if (!rawValue) return null;
      return optionByKey.get(rawValue.toLowerCase()) || {
        ...CUSTOM_RESULT_FALLBACK,
        value: rawValue,
        label: rawValue,
      };
    })
    .filter(Boolean);
}
